import { forwardRef } from 'react';

import { Skeleton as MuiSkeleton } from '@mui/material';
import { Skeleton as DsSkeleton, cn } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';

import { toDsProps } from './toDsProps';

import type { SkeletonProps as MuiSkeletonProps } from '@mui/material';

export type SkeletonProps = MuiSkeletonProps & {
  /** Typed because hyphenated JSX attributes are unchecked; `toDsProps` has to see it. */
  'data-testid'?: string;
};

export const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>((props, ref) => {
  // The visual-refresh gate lives here, not at the call sites. `null` falls through to MUI.
  const dsProps = VISUAL_REFRESH ? toDsProps(props) : null;

  if (!dsProps) return <MuiSkeleton ref={ref} {...props} />;

  const { shape, className, style, testId, inline, fitsLine, scalesBar } = dsProps;

  // A MUI text skeleton is as tall as the line it stands in, with the bar drawn at 60% of it. The DS
  // bar is a fixed 16, so it sits centred in a box of the parent's line height (`1lh`) and nothing
  // moves when the real text arrives. Given a height, MUI still paints 60% of it, so the bar does too.
  if (fitsLine || scalesBar) {
    return (
      <span
        ref={ref}
        data-testid={testId}
        className={cn(fitsLine && 'h-[1lh]', 'items-center', inline ? 'inline-flex' : 'flex', className)}
        style={style}
      >
        <DsSkeleton shape={shape} className={cn(scalesBar && 'h-[60%]')} />
      </span>
    );
  }

  return (
    <DsSkeleton
      ref={ref}
      shape={shape}
      data-testid={testId}
      className={cn(inline && 'inline-block', className)}
      style={style}
    />
  );
});
